import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IFormDelegate, IFormOptions } from '@modeso/aumico-lib-core-fe';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { AS_FIRST_ELEMENT_OPTION_ID } from '../../../constants/subtotal-form.constant';
import { IAddSubtotalFormDialogData } from '../../../interfaces/add-subtotal-form-dialog-data.interface';
import { IAddSubtotalFormDialogResult } from '../../../interfaces/add-subtotal-form-dialog-result.interface';

@UntilDestroy()
@Component({
    selector: 'templates-add-subtotal-form',
    templateUrl: './add-subtotal-form.dialog.html',
    styleUrls: ['./add-subtotal-form.dialog.scss'],
})
export class AddSubtotalFormDialogComponent {
    public options: IFormOptions;
    public formDelegate: IFormDelegate;

    public disableSubmitButton = true;

    constructor(
        public dialogRef: MatDialogRef<AddSubtotalFormDialogComponent, IAddSubtotalFormDialogResult | undefined>,
        private localizedTexts: I18NextService,
        @Inject(MAT_DIALOG_DATA) public data: IAddSubtotalFormDialogData,
    ) {}

    ngOnInit(): void {
        this.addAsFirstElementOptionInPositionOptions();
        this.prepareFields();
        const that = this;
        this.formDelegate = {
            onSubmit(result: IAddSubtotalFormDialogResult) {
                that.savePressed(result);
            },
        };

        if (this.data.subTotalSaveData != null) {
            // if the saved scope group does not exist in the options then disable save button
            this.disableSubmitButton = !this.isScopedGroupExistsInOptions();
        }
    }

    private isScopedGroupExistsInOptions(): boolean {
        const scope = this.data.groupOptions.find((group) => group.id === this.data.subTotalSaveData.scopedGroup.id);
        return scope != null;
    }

    public handleFormValidity(isFormValid: boolean): void {
        this.disableSubmitButton = !isFormValid;
    }

    private prepareFields(): void {
        this.options = {
            properties: [
                {
                    name: this.localizedTexts.t('aumico_templates_subtotal_name') as string,
                    type: 'text',
                    property: 'subtotalName',
                    required: true,
                },
                {
                    name: this.localizedTexts.t('aumico_templates_subtotal_scope') as string,
                    type: 'select',
                    multiple: false,
                    options: this.data.groupOptions,
                    optionsLabel: this.localizedTexts.t('aumico_templates_fli_form_group_options_label') as string,
                    property: 'scopedGroup',
                    required: true,
                },
                {
                    name: this.localizedTexts.t('aumico_templates_subtotal_position_after') as string,
                    type: 'select',
                    multiple: false,
                    options: this.data.flisAndGroupsPositionOptions,
                    optionsLabel: this.localizedTexts.t('aumico_templates_subtotal_select_position') as string,
                    property: 'positionBeforeSubtotal',
                    required: true,
                },
            ],
            submitText: this.localizedTexts.t('aumico_templates_save') as string,
            invalidText: this.localizedTexts.t('aumico_templates_fli_form_error_name_already_exists') as string,
        };
    }

    private addAsFirstElementOptionInPositionOptions(): void {
        const asFirstElementOption: INameId = {
            id: AS_FIRST_ELEMENT_OPTION_ID,
            name: this.localizedTexts.t('aumico_templates_subtotal_select_position_as_first_element') as string,
        };
        this.data.flisAndGroupsPositionOptions.unshift(asFirstElementOption);
    }

    private savePressed(result: IAddSubtotalFormDialogResult) {
        const index = this.data.allItemsInSelectedScope.findIndex((element) => {
            return element.name === result.positionBeforeSubtotal.id;
        });

        this.dialogRef.close({
            subtotalName: result.subtotalName,
            scopedGroup: result.scopedGroup,
            positionBeforeSubtotal: result.positionBeforeSubtotal,
            positionBeforeSubtotalIndex: index,
        } as IAddSubtotalFormDialogResult);
    }

    public closeDialog(): void {
        this.dialogRef.close();
    }
}
